// LIBRERIE/IMPORTS
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue } from "react-select";
import ReactSlider from "react-slider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleDown } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetProperties } from "../../hooks/useGetProperties";
import { useGetAddresses } from "../../hooks/useGetAddresses";

// CONFIGURAZIONI
import { AGENCY_ID } from "../../config/appConfig";
import { contractTranslations, getContractTranslation, LanguageCode } from "../../config/i18n/elementsMappingProperty";
import { getTipologiaTranslation } from "../../config/i18n/propertyTranslations";

// COMPONENTI
import LoadingIndicator from "../Loaders/LoadingIndicator";

interface Property {
    contractDetails?: { id: number; name: string };
    tipologyDetails?: { id: number; name: string };
}

{/*
interface AddressData {
    province: { codiceIstat: string | null; nome: string | null }[];
    comuni: { codiceIstatProvincia?: string; codiceIstatComuneAlfa?: string; nomeIt?: string }[];
    frazioni: { comuneIstat?: string; id?: number; frazione?: string }[];
}
*/}

interface SearchProps {
    searchData?: SearchData;
    showPriceSlider: boolean;
}

interface SearchData {
    giardino?: boolean;
    terrazzo?: boolean;
    piscina?: boolean;
    riferimento?: string;
}

interface OptionType {
    readonly value: number | string | null;
    readonly label: string;
}

interface FrazioneOption extends OptionType {
    readonly value: string;
    readonly isDisabled?: boolean;
}

const PropertySearchField: React.FC<SearchProps> = ({ searchData = {}, showPriceSlider }) => {
    // --- Hooks e State ---
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Stato per i filtri e UI
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
    const [selectedTipologia, setSelectedTipologia] = useState<readonly OptionType[]>([]);
    const [selectedComune, setSelectedComune] = useState<readonly OptionType[]>([]);
    const [selectedProvincia, setSelectedProvincia] = useState<OptionType | null>(null);
    const [selectedFrazione, setSelectedFrazione] = useState<FrazioneOption[]>([]);
    const [camereMax, setCamereMax] = useState<number | "">("");
    const [bagniMax, setBagniMax] = useState<number | "">("");
    const [sqmtMin, setSqmtMin] = useState<number | "">("");
    const [sqmtMax, setSqmtMax] = useState<number | "">("");
    const [priceMin, setPriceMin] = useState<number | "">("");
    const [priceMax, setPriceMax] = useState<number | "">("");
    const [priceRange, setPriceRange] = useState<readonly [number, number]>([0, 2000000]);
    const [localSearchData, setLocalSearchData] = useState<SearchData>(searchData);

    // Stato per dati caricati
    const [allTipologie, setAllTipologie] = useState<OptionType[]>([]);
    const [initialProperties, setInitialProperties] = useState<readonly Property[]>([]);

    // Ref per scroll
    const advancedSearchRef = useRef<HTMLDivElement>(null);

    // Costanti
    const CAPOLUOGO_VALUE = "30000";


    // --- useGetProperties e useGetAddresses (senza filtri) ---
    const { properties: unfilteredProperties, loading: initialLoading, error: initialError } = useGetProperties(AGENCY_ID, {
        limit: 100000000,
        offset: 0,
        withCount: false,
        filters: {
            contract: [],
            type: [],
            provincia: '',
            comune: [],
            frazione: [],
            minPrice: 0,
            maxPrice: 2000000,
            sqmtMin: 0,
            gardenPrivate: false,
            terrace: false,
            swimmingPool: false,
            ref: '',
        },
    });

    const { data: addresses, loading: loadingAddresses, error: errorAddresses } = useGetAddresses(AGENCY_ID);


    // --- Effetti Collaterali ---
    // Imposta le proprietà iniziali una volta caricate
    useEffect(() => {
        if (unfilteredProperties) {
            setInitialProperties(unfilteredProperties as readonly Property[]);
        }
    }, [unfilteredProperties]);

    // Estrai e imposta le tipologie uniche dalle proprietà caricate
    useEffect(() => {
        if (initialProperties.length > 0) {
            const uniqueTipologyIds = new Set<number>();
            initialProperties.forEach(property => {
                const tipologiaValue = property.tipologyDetails?.id;
                if (tipologiaValue && typeof tipologiaValue === 'number') {
                    uniqueTipologyIds.add(tipologiaValue);
                }
            });

            // Mappa gli ID univoci alle opzioni tradotte
            const uniqueTipologieArray = Array.from(uniqueTipologyIds)
                .map(id => ({
                    value: id,
                    label: getTipologiaTranslation(id, i18n.language as LanguageCode)
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            const tipologieOptions: OptionType[] = [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }, ...uniqueTipologieArray];


            setAllTipologie(tipologieOptions);
        } else if (initialProperties.length === 0 && !initialLoading) {
             setAllTipologie([{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }]);
        }
    }, [initialProperties, i18n.language, initialLoading, t]);


    // --- Funzioni Helper ---
    const isCapoluogoSelected = (): boolean => {
        return selectedFrazione.some(frazione => frazione.value === CAPOLUOGO_VALUE);
    };

    // --- Generazione Opzioni per Select ---

    const contratti: readonly OptionType[] = [
        { value: null, label: t('common.(any)') },
        ...contractTranslations
            .map(contract => ({
                value: contract.id,
                label: getContractTranslation(contract.id, i18n.language as LanguageCode)
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    ];

    // Opzioni Province
    const provinceOptions: readonly OptionType[] = [
        { value: null, label: t('common.(any)') ?? '(qualsiasi)' },
        ...(addresses?.province
            .map((provincia) => ({
                value: provincia.codiceIstat ?? '',
                label: provincia.nome ?? t('common.notAvailable') ?? 'N/D',
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) ?? [])
    ];


    // Opzioni Comuni (filtrati per provincia selezionata)
    const comuneOptions: readonly OptionType[] = [
        { value: null, label: t('common.(any)') ?? '(qualsiasi)' },
        ...(selectedProvincia?.value ?
            (addresses?.comuni
                .filter((comune) => comune.codiceIstatProvincia === selectedProvincia.value)
                .map((comune) => ({
                    value: comune.codiceIstatComuneAlfa ?? "",
                    label: comune.nomeIt ?? t('common.notAvailable') ?? 'N/D',
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) ?? [])
            : [])
    ];

    // Opzioni Frazioni (filtrate per comune/i selezionato/i)
    const getFrazioneOptions = (): readonly FrazioneOption[] => {
        const showCapoluogoOption = selectedComune.length > 0; // Mostra CAPOLUOGO se almeno un comune è selezionato

        const frazioniBase: FrazioneOption[] = (selectedComune.length > 0 && addresses?.frazioni // Calcola solo se comuni selezionati
            ? addresses.frazioni
                .filter((frazione) => selectedComune.some(c => c.value === frazione.comuneIstat)) // Filtra per i comuni selezionati
                .map((frazione) => ({
                    value: frazione.id?.toString() ?? "", // Assicura sia stringa
                    label: frazione.frazione ?? t('common.notAvailable'),
                    isDisabled: false,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
            : []) ?? [];

        const options: FrazioneOption[] = [
            ...(showCapoluogoOption ? [{ value: CAPOLUOGO_VALUE, label: t('searchWidget.capoluogoLabel'), isDisabled: false }] : []),
            ...frazioniBase,
        ];

        // Logica per disabilitare opzioni
        const disableAll = selectedComune.length > 1; // Disabilita se più comuni selezionati
        const disableNonCapoluogo = isCapoluogoSelected(); // Disabilita altre se CAPOLUOGO selezionato

        return options.map(frazione => ({
            ...frazione,
            isDisabled: frazione.isDisabled || disableAll || (disableNonCapoluogo && frazione.value !== CAPOLUOGO_VALUE)
        }));
    };


    // --- Handlers Eventi ---
    // Handler per il toggle della ricerca avanzata
    const toggleAdvancedSearch = (event: React.MouseEvent) => {
        event.preventDefault();
        setIsAdvancedSearchOpen(prev => !prev);
    };

    const handleContractChange = (selectedOption: OptionType | null) => {
        // Imposta solo l'ID nel tuo stato
        setSelectedContractId(selectedOption?.value as number | null ?? null);
    };

    // Handlers per i Select (usano OptionType generico)
    const handleTipologiaChange = (newValue: MultiValue<OptionType>) => {
        setSelectedTipologia(newValue as readonly OptionType[]);
    };

    const handleProvinciaChange = (selectedOption: OptionType | null) => {
        setSelectedProvincia(selectedOption);
        setSelectedComune([]);
        setSelectedFrazione([]);
    };

    const handleComuneChange = (newValue: MultiValue<OptionType>) => {
        setSelectedComune(newValue as readonly OptionType[]);
        setSelectedFrazione([]); // Resetta frazione
    };

    // Handler specifico per Frazioni che gestisce la conversione a FrazioneOption
    const handleFrazioneChange = (newValue: MultiValue<OptionType>) => {
        const newFrazioneValues = (Array.isArray(newValue) ? newValue : [])
            .filter(option => option.value !== null && typeof option.value === 'string') // Assicura valore stringa non nullo
            .map(option => ({
                value: option.value as string, // Cast a string
                label: option.label,
                // Cerca l'opzione originale per mantenere isDisabled (più robusto che fare cast)
                isDisabled: getFrazioneOptions().find(fo => fo.value === option.value)?.isDisabled ?? false
            })) as FrazioneOption[]; // Tipo finale

        // Logica per selezionare solo CAPOLUOGO se scelto
        if (newFrazioneValues.some(frazione => frazione.value === CAPOLUOGO_VALUE)) {
            setSelectedFrazione(newFrazioneValues.filter(frazione => frazione.value === CAPOLUOGO_VALUE));
        } else {
            setSelectedFrazione(newFrazioneValues);
        }
    };

    // Handlers per input numerici
    const handleNumericInputChange = (
        setter: React.Dispatch<React.SetStateAction<number | "">>
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setter(value === "" ? "" : parseInt(value, 10) || 0); // Converte in numero, fallback a 0 se NaN
    };

    const handleCamereMaxChange = handleNumericInputChange(setCamereMax);
    const handleBagniMaxChange = handleNumericInputChange(setBagniMax);
    const handleSqmtMinChange = handleNumericInputChange(setSqmtMin);
    const handleSqmtMaxChange = handleNumericInputChange(setSqmtMax);
    const handlePriceMinChange = handleNumericInputChange(setPriceMin);
    const handlePriceMaxChange = handleNumericInputChange(setPriceMax);

    // Handler per lo Slider
    const handlePriceChange = (values: number | readonly number[]) => {
        // Verifica che sia un array e abbia esattamente 2 elementi
        if (Array.isArray(values) && values.length === 2
            // Verifica opzionale ma più sicura: controlla che entrambi siano numeri
            && typeof values[0] === 'number' && typeof values[1] === 'number') {
            // Converti prima a unknown, poi alla tupla desiderata
            setPriceRange(values as unknown as readonly [number, number]);
        }
    };


    // Handler per il campo Riferimento
    const handleRiferimentoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearchData(prev => ({ ...prev, riferimento: event.target.value }));
    };

    // Handler per le Checkbox (aggiorna localSearchData)
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setLocalSearchData(prev => ({ ...prev, [name as keyof SearchData]: checked }));
    };


    // --- Submit Form ---
    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault(); // Previene il submit HTML standard
        const params = new URLSearchParams(); // Oggetto per costruire la query string

        if (selectedContractId !== null) {
            params.set('contratto', String(selectedContractId));
        }

        selectedTipologia.forEach(tipologia => { if (tipologia.value !== null) params.append('tipologia', String(tipologia.value)); });

        if (selectedProvincia && selectedProvincia.value !== null) {
            params.set('provincia', String(selectedProvincia.value));
        }

        selectedComune.forEach(comune => { if (comune.value !== null) params.append('comune', String(comune.value)); });

        // Gestione Frazioni (CAPOLUOGO)
        if (isCapoluogoSelected()) {
            params.set('frazione', CAPOLUOGO_VALUE);
        } else {
            selectedFrazione.forEach(frazione => { if (frazione.value) params.append('frazione', String(frazione.value)); });
        }

        // Parametri numerici
        if (camereMax !== "") params.set('camere_max', String(camereMax));
        if (bagniMax !== "") params.set('bagni_max', String(bagniMax));
        if (sqmtMin !== "") params.set('mq_min', String(sqmtMin));
        if (sqmtMax !== "") params.set('mq_max', String(sqmtMax));

        // Range Prezzo (solo se diversi dai default)
        if (showPriceSlider) {
            if (priceRange[0] > 0) params.set('prezzo_min', String(priceRange[0]));
            if (priceRange[1] < 2000000) params.set('prezzo_max', String(priceRange[1]));
        } else {
            if (priceMin > "0") params.set('prezzo_min', String(priceMin));
            if (priceMax > "0") params.set('prezzo_max', String(priceMax));
        }


        // Parametri testuali e booleani
        if (localSearchData.riferimento) params.set('riferimento', localSearchData.riferimento);
        if (localSearchData.giardino) params.set('giardino_privato', 'true');
        if (localSearchData.terrazzo) params.set('terrazzo', 'true');
        if (localSearchData.piscina) params.set('piscina', 'true');

        // Naviga alla pagina dei risultati con i parametri
        const queryString = params.toString();
        const relativePath = `/immobili${queryString ? '?' + queryString : ''}`;

        // console.log('Parametri generati:', queryString);
        // console.log('Navigazione verso:', relativePath);
        navigate(relativePath);
    };

    // --- Rendering ---
    if (initialLoading || loadingAddresses) return <LoadingIndicator />;
    if (initialError || errorAddresses) {
        console.error("Errore caricamento dati ricerca:", initialError || errorAddresses);
        return <div className="alert alert-danger">{t('validation.loadingError', 'Errore durante il caricamento dei dati necessari.')}</div>;
    }

    // --- Variabili per Traduzioni Comuni (per pulizia JSX) ---
    const labelContract = t('searchWidget.contract');
    const labelType = t('searchWidget.type');
    const labelProvince = t('searchWidget.province');
    const labelCity = t('searchWidget.city');
    const labelFraction = t('searchWidget.fraction');
    const labelRooms = t('common.rooms');
    const labelBathrooms = t('common.bathrooms');
    const labelPrice = t('common.price');
    const labelPriceMin = t('common.priceMin');
    const labelPriceMax = t('common.priceMax');
    const labelSqmtMin = t('property.sqmtMin');
    const labelSqmtMax = t('property.sqmtMax');
    const labelReference = t('property.referenceExtended');
    const labelOther = t('common.other');
    const labelGarden = t('property.garden.label');
    const labelTerrace = t('property.terrace');
    const labelPool = t('property.pool');
    const labelSearch = t('common.search');
    const labelAdvancedSearch = t('searchWidget.advancedSearchTitle');
    const placeholderAny = t('common.(any)');
    const placeholderType = t('searchWidget.type');
    const placeholderCity = t('searchWidget.city');
    const placeholderFraction = t('searchWidget.fraction');
    const placeholderRoomsMax = t('searchWidget.fieldMax', { field: labelRooms });
    const placeholderPriceMin = labelPriceMin;
    const placeholderPriceMax = labelPriceMax;
    const placeholderBathroomsMax = t('searchWidget.fieldMax', { field: labelBathrooms });
    const msgNoResults = t('common.noResults', 'Nessun risultato');
    const msgSelectProvinceFirst = t('searchWidget.selectProvinceFirst', 'Seleziona prima una provincia');
    const msgSelectOneCityFirst = t('searchWidget.selectOneCityFirst', 'Seleziona un solo comune');

    const currentContractOption = contratti.find(c => c.value === selectedContractId) || null;

    return (
        <div className="property-search-field bg-white">
            <div className="property-search-item">

                <form
                    id="search"
                    className="form-row basic-select-wrapper align-items-end"
                    onSubmit={handleSubmit}
                    noValidate
                >

                    {/* Contratto */}
                    <div className="form-group col-12 col-md-6 col-lg-3">
                        <label htmlFor="select_contratto">{labelContract}</label>
                        <Select<OptionType, false>
                            inputId="select_contratto"
                            name="contratto"
                            options={contratti}
                            value={currentContractOption} // Usa lo stato basato sull'ID
                            onChange={handleContractChange}
                            placeholder={placeholderAny}
                            className="basic-select"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            aria-label={labelContract ?? ''}
                        />
                    </div>

                    {/* Tipologia */}
                    <div className="form-group col-12 col-md-6 col-lg-3">
                        <label htmlFor="select_tipologia">{labelType}</label>
                        <Select<OptionType, true>
                            inputId="select_tipologia"
                            name="tipologia"
                            isMulti
                            options={allTipologie}
                            value={selectedTipologia}
                            onChange={handleTipologiaChange}
                            placeholder={placeholderType}
                            closeMenuOnSelect={false}
                            className="basic-select"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            aria-label={labelType ?? ''}
                        />
                    </div>

                    {/* Provincia */}
                    <div className="form-group col-12 col-md-6 col-lg-3">
                        <label htmlFor="select_provincia">{labelProvince}</label>
                        <Select<OptionType, false>
                            inputId="select_provincia"
                            name="provincia"
                            value={selectedProvincia}
                            onChange={handleProvinciaChange}
                            options={provinceOptions}
                            placeholder={placeholderAny}
                            className="basic-select"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            aria-label={labelProvince ?? ''}
                        />
                    </div>

                    {/* Comune */}
                    <div className="form-group col-12 col-md-6 col-lg-3">
                        <label htmlFor="select_comune">{labelCity}</label>
                        <Select<OptionType, true>
                            inputId="select_comune"
                            name="comune"
                            isMulti
                            value={selectedComune}
                            onChange={handleComuneChange}
                            options={comuneOptions}
                            isDisabled={!selectedProvincia?.value} // Disabilita se provincia non selezionata
                            placeholder={placeholderCity}
                            closeMenuOnSelect={false}
                            className="basic-select"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            aria-label={labelCity ?? ''}
                            noOptionsMessage={() => selectedProvincia?.value ? msgNoResults : msgSelectProvinceFirst}
                        />
                    </div>

                    {/*Prezzo minimo */}
                    <div className="form-group col-12 col-md-6 col-lg">
                        <label htmlFor="input_price_min">{placeholderPriceMin}</label>
                        <input
                            type="number"
                            id="input_price_min"
                            name="price_min"
                            className="form-control"
                            min="0"
                            step="10000"
                            value={priceMin}
                            onChange={handlePriceMinChange}
                            placeholder={labelPriceMin}
                            aria-label={placeholderPriceMin ?? ''}
                        />
                    </div>

                    {/*Prezzo minimo */}
                    <div className="form-group col-12 col-md-6 col-lg">
                        <label htmlFor="input_price_min">{placeholderPriceMax}</label>
                        <input
                            type="number"
                            id="input_price_max"
                            name="price_max"
                            className="form-control"
                            max="2000000"
                            step="10000"
                            value={priceMax}
                            onChange={handlePriceMaxChange}
                            placeholder={labelPriceMax}
                            aria-label={placeholderPriceMax ?? ''}
                        />
                    </div>

                    {/* Prezzo Range Slider */}
                    {showPriceSlider &&
                        <div className="form-group property-price-slider col-12 col-md-6 col-lg-5">
                            <label id="price-range-label">{labelPrice}</label>
                            <div className="slider-container">
                                {/* Etichette nascoste per accessibilità slider */}
                                <span id="price-min-label" style={{ display: 'none' }}>{t('property.minPriceLabel', 'Prezzo minimo')}</span>
                                <span id="price-max-label" style={{ display: 'none' }}>{t('property.maxPriceLabel', 'Prezzo massimo')}</span>
                                <ReactSlider
                                    min={0}
                                    max={2000000}
                                    step={10000}
                                    onChange={handlePriceChange}
                                    value={priceRange}
                                    className="price-range-slider"
                                    thumbClassName="slider-thumb"
                                    trackClassName="slider-track"
                                    ariaLabelledby={['price-min-label', 'price-max-label']}
                                    renderTrack={(props, state) => {

                                        const trackClass = state.index === 1 ? 'slider-track selected' : 'slider-track';
                                        return <div {...props} className={trackClass} />;
                                    }}
                                />
                                <div className="d-flex justify-content-between slider-prices">
                                    <span className="price-label left">{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(priceRange[0])}</span>
                                    <span className="price-label right">{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(priceRange[1])}</span>
                                </div>
                            </div>
                        </div>
                    }

                    {/* Riferimento */}
                    <div className="form-group col-12 col-md-6 col-lg-3">
                        <label htmlFor="input_riferimento">{labelReference}</label>
                        <input
                            type="text"
                            id="input_riferimento"
                            name="riferimento"
                            value={localSearchData.riferimento || ""}
                            onChange={handleRiferimentoChange}
                            className="form-control"
                            placeholder={labelReference ?? ''}
                            aria-label={labelReference ?? ''}
                        />
                    </div>

                    {/* Bottone Cerca Desktop */}
                    <div id="btn-pc" className="d-none d-md-block form-group col-md-6 col-lg-3">
                        <span className="align-items-center d-lg-flex z101 w-100">
                            <button className="btn btn-primary text-uppercase w-100" type="submit">
                                <i className="fas fa-search mr-1" aria-hidden="true"></i>
                                <span>{labelSearch}</span>
                            </button>
                        </span>
                    </div>

                    {/* Toggle Ricerca Avanzata */}
                    <div className="advanced-button">
                        <a
                            className="more-search p-0"
                            href="#advanced-search"
                            title={labelAdvancedSearch ?? ''}
                            role="button"
                            aria-expanded={isAdvancedSearchOpen}
                            aria-controls="advanced-search"
                            onClick={toggleAdvancedSearch}
                        >
                            <span className="d-block pr-2 mb-1"></span>
                            <i>
                                <FontAwesomeIcon icon={faAngleDoubleDown} />
                            </i>
                        </a>
                    </div>

                    {/* Sezione Ricerca Avanzata */}
                    <div
                        className={`collapse advanced-search col-12 ${isAdvancedSearchOpen ? "show" : ""}`}
                        id="advanced-search"
                        ref={advancedSearchRef}
                    >
                        <div className="form-row card">

                            {/* Frazione */}
                            <div className="form-group col-12 col-md-6 col-lg-3">
                                <label htmlFor="select_frazione">{labelFraction}</label>
                                <Select<FrazioneOption, true>
                                    inputId="select_frazione"
                                    name="frazione"
                                    isMulti
                                    value={selectedFrazione}
                                    onChange={handleFrazioneChange}
                                    options={getFrazioneOptions()}
                                    isDisabled={!selectedProvincia?.value || selectedComune.length === 0 || selectedComune.length > 1}
                                    placeholder={placeholderFraction}
                                    closeMenuOnSelect={false}
                                    className="basic-select"
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    aria-label={labelFraction ?? ''}
                                    noOptionsMessage={() => !selectedProvincia?.value || selectedComune.length === 0 ? msgSelectOneCityFirst : msgNoResults}
                                />
                            </div>

                            {/* Camere Max */}
                            <div className="form-group col-12 col-md-6 col-lg-3">
                                <label htmlFor="input_bagni_max">{placeholderRoomsMax}</label>
                                <input
                                    type="number"
                                    id="input_camere_max"
                                    name="camere_max"
                                    className="form-control"
                                    min="0"
                                    step="1"
                                    value={camereMax}
                                    onChange={handleCamereMaxChange}
                                    placeholder={labelRooms}
                                    aria-label={placeholderRoomsMax ?? ''}
                                />
                            </div>

                            {/* Bagni Max */}
                            <div className="form-group col-12 col-md-6 col-lg-3">
                                <label htmlFor="input_bagni_max">{placeholderBathroomsMax}</label>
                                <input
                                    type="number"
                                    id="input_bagni_max"
                                    name="bagni_max"
                                    className="form-control"
                                    min="0"
                                    step="1"
                                    value={bagniMax}
                                    onChange={handleBagniMaxChange}
                                    placeholder={labelBathrooms}
                                    aria-label={placeholderBathroomsMax ?? ''}
                                />
                            </div>

                            {/* Inputs nascosti non sono più strettamente necessari se si usa handleSubmit */}
                            {/* <input type="hidden" name="prezzo_min" value={priceRange[0]} /> */}
                            {/* <input type="hidden" name="prezzo_max" value={priceRange[1]} /> */}

                            {/* Mq Min */}
                            <div className="form-group col-12 col-md-6 col-lg-3">
                                <label htmlFor="input_mq_min">{labelSqmtMin}</label>
                                <input
                                    type="number"
                                    id="input_mq_min"
                                    name="mq_min"
                                    className="form-control"
                                    min="0"
                                    step="10"
                                    value={sqmtMin}
                                    onChange={handleSqmtMinChange}
                                    placeholder={labelSqmtMin ?? ''}
                                    aria-label={labelSqmtMin ?? ''}
                                />
                            </div>

                            {/* Mq Max */}
                            <div className="form-group col-12 col-md-6 col-lg-3">
                                <label htmlFor="input_mq_max">{labelSqmtMax}</label>
                                <input
                                    type="number"
                                    id="input_mq_max"
                                    name="mq_max"
                                    className="form-control"
                                    min="0"
                                    step="10"
                                    value={sqmtMax}
                                    onChange={handleSqmtMaxChange}
                                    placeholder={labelSqmtMax ?? ''}
                                    aria-label={labelSqmtMax ?? ''}
                                />
                            </div>

                            {/* Altro (Checkboxes) */}
                            <div className="form-group col-12 col-md-6 col-lg">
                                <div className="form-group-search">
                                    <label>{labelOther}</label>
                                    <div className="d-flex">

                                        {/* Giardino */}
                                        <div className="form-check form-switch col-lg custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="giardino" // ID semplice
                                                name="giardino" // Corrisponde a chiave in SearchData
                                                className="custom-control-input"
                                                checked={!!localSearchData.giardino} // Usa !! per cast a booleano
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="custom-control-label" htmlFor="giardino">{labelGarden}</label>
                                        </div>

                                        {/* Terrazzo */}
                                        <div className="form-check form-switch col-lg custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="terrazzo"
                                                name="terrazzo"
                                                className="custom-control-input"
                                                checked={!!localSearchData.terrazzo}
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="custom-control-label" htmlFor="terrazzo">{labelTerrace}</label>
                                        </div>

                                        {/* Piscina */}
                                        <div className="form-check form-switch col-lg custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="piscina"
                                                name="piscina"
                                                className="custom-control-input"
                                                checked={!!localSearchData.piscina}
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="custom-control-label" htmlFor="piscina">{labelPool}</label>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- Bottone Cerca Mobile --- */}
                    <div id="btn-mobile" className={`d-md-none btn-block btn-mobile m-3 mb-30imp z101 ${isAdvancedSearchOpen ? 'order-last' : ''}`}>
                        <button className="btn btn-primary btn-block d-flex align-items-center justify-content-center" type="submit">
                            <i className="fas fa-search mr-1" aria-hidden="true"></i>
                            <span>{labelSearch}</span>
                        </button>
                    </div>

                </form>

            </div >
        </div >
    );
};

export default PropertySearchField;